import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useCurrency } from '../../hooks/useCurrency';
import { Customer, CustomerType } from '../../types';
import { customerService } from '../../services/customerService';
import { X, User, AlertCircle, CreditCard, ShoppingCart, CheckCircle2, XCircle } from 'lucide-react';
import { customToast } from '../../utils/customToast';

interface CustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'create' | 'edit' | 'view';
  customer?: Customer | null;
  onSuccess: () => void;
  isLoading?: boolean;
}

const CustomerModal: React.FC<CustomerModalProps> = ({
  isOpen,
  onClose,
  mode,
  customer,
  onSuccess,
  isLoading = false
}) => {
  const { theme } = useTheme();
  const { formatCurrency } = useCurrency();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isClosing, setIsClosing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    customer_type: 'retail' as CustomerType,
    is_active: true
  });
  
  // Additional state for view mode data
  const [customerSales, setCustomerSales] = useState<any[]>([]);
  const [paymentHistory, setPaymentHistory] = useState<any[]>([]);
  const [dataLoading, setDataLoading] = useState(false);

  // Initialize form data when customer changes
  useEffect(() => {
    if (customer && (mode === 'edit' || mode === 'view')) {
      setFormData({
        name: customer.name || '',
        phone: customer.phone || '',
        address: customer.address || '',
        customer_type: customer.customer_type || 'retail',
        is_active: customer.is_active !== undefined ? customer.is_active : true
      });
      
      // Load additional data for view mode
      if (mode === 'view') {
        loadCustomerData();
      }
    } else {
      setFormData({
        name: '',
        phone: '',
        address: '',
        customer_type: 'retail',
        is_active: true
      });
    }
    setErrors({}); // Clear errors when modal opens/mode changes
  }, [customer, mode, isOpen]);

  // Load customer sales and payment history for view mode
  const loadCustomerData = async () => {
    if (!customer?.id) return;
    
    setDataLoading(true);
    try {
      const [sales, payments] = await Promise.all([
        customerService.getCustomerSales(customer.id),
        customerService.getPaymentHistory(customer.id)
      ]);
      
      setCustomerSales(sales || []);
      setPaymentHistory(payments || []);
    } catch (error) {
      console.error('Error loading customer data:', error);
      customToast.error('Failed to load customer details');
    } finally {
      setDataLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      address: '',
      customer_type: 'retail',
      is_active: true
    });
    setErrors({});
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Customer name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      customToast.error('Please fix the validation errors');
      return;
    }

    try {
      setLoading(true);
      
      if (mode === 'create') {
        await customerService.createCustomer(formData);
        customToast.success('Customer created successfully');
        resetForm(); // Clear form after successful creation
      } else if (mode === 'edit' && customer) {
        await customerService.updateCustomer({ id: customer.id, ...formData });
        customToast.success('Customer updated successfully');
      }
      
      onSuccess();
      handleClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Operation failed';
      customToast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    // Convert string 'true'/'false' to boolean for is_active
    const finalValue = field === 'is_active' && typeof value === 'string' 
      ? value === 'true' 
      : value;
    
    setFormData(prev => ({ ...prev, [field]: finalValue }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 150); // Match animation duration
  };

  if (!isOpen && !isClosing) return null;

  const title = mode === 'create' ? 'Add New Customer' : mode === 'edit' ? 'Edit Customer' : 'Customer Details';

  return createPortal(
    <div 
      className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-[9999] ${
        isClosing ? 'animate-fadeOut' : 'animate-fadeIn'
      }`}
      onClick={handleOverlayClick}
    >
      <div className={`w-full max-w-2xl max-h-[90vh] flex flex-col rounded-xl shadow-2xl ${
        isClosing ? 'animate-scaleOut' : 'animate-scaleIn'
      } ${theme === 'dark' ? 'bg-slate-800' : 'bg-white'
      }`}>
        {/* Header */}
        <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-b flex-shrink-0 rounded-t-xl ${
          theme === 'dark' ? 'border-slate-700 bg-slate-800/95' : 'border-slate-200 bg-gray-50/95'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 min-w-0 flex-1">
              <div className={`p-1.5 rounded-lg flex-shrink-0 ${
                theme === 'dark' ? 'bg-blue-900/50' : 'bg-blue-100'
              }`}>
                <User className={`w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 ${
                  theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                }`} />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className={`text-sm sm:text-base md:text-lg font-semibold truncate ${
                  theme === 'dark' ? 'text-white' : 'text-slate-900'
                }`}>
                  {title}
                </h2>
                <p className={`text-xs truncate ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  {mode === 'create' ? 'Enter customer details' : mode === 'edit' ? 'Update customer information' : 'View customer information'}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className={`p-2 rounded-lg transition-colors touch-manipulation flex-shrink-0 ${
                theme === 'dark' 
                  ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' 
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable Content Body */}
        <div className={`flex-1 overflow-y-auto ${
          theme === 'dark' ? 'bg-slate-800' : 'bg-white'
        }`}>
          <div className="p-4 sm:p-5 md:p-6">
          {isLoading ? (
            /* Loading skeleton for both view and form while parent fetches customer */
            <div className="space-y-4">
              <div className={`p-2.5 sm:p-3 md:p-4 rounded-lg border ${
                theme === 'dark' ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="h-4 w-1/3 rounded-md bg-gray-300 dark:bg-slate-700 animate-pulse" />
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="h-4 rounded bg-gray-300 dark:bg-slate-700 animate-pulse" />
                  <div className="h-4 rounded bg-gray-300 dark:bg-slate-700 animate-pulse" />
                  <div className="h-4 rounded bg-gray-300 dark:bg-slate-700 animate-pulse sm:col-span-2" />
                </div>
              </div>

              <div className={`rounded-lg border p-3 ${theme === 'dark' ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                <div className="h-36 rounded bg-gray-300 dark:bg-slate-700 animate-pulse" />
              </div>

              <div className={`rounded-lg border p-3 ${theme === 'dark' ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                <div className="h-24 rounded bg-gray-300 dark:bg-slate-700 animate-pulse" />
              </div>
            </div>
          ) : mode === 'view' ? (
            /* View Mode - Enhanced Customer Details */
            <div className="space-y-3 sm:space-y-4 animate-content-fade">
              {/* Customer Information Card */}
              <div className={`p-2.5 sm:p-3 md:p-4 rounded-lg border ${
                theme === 'dark'
                  ? 'bg-slate-700/50 border-slate-600'
                  : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-center space-x-2 mb-2 sm:mb-3">
                  <User className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${
                    theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                  }`} />
                  <h3 className={`text-sm sm:text-base font-semibold ${
                    theme === 'dark' ? 'text-white' : 'text-slate-900'
                  }`}>
                    Customer Information
                  </h3>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
                  <div>
                    <p className={`text-xs font-medium mb-0.5 sm:mb-1 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      Name
                    </p>
                    <p className={`text-xs sm:text-sm break-words ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      {customer?.name || 'N/A'}
                    </p>
                  </div>
                  
                  <div>
                    <p className={`text-xs font-medium mb-0.5 sm:mb-1 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      Phone
                    </p>
                    <p className={`text-sm sm:text-base break-words ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      {customer?.phone || 'N/A'}
                    </p>
                  </div>
                  
                  <div>
                    <p className={`text-xs font-medium mb-0.5 sm:mb-1 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      Customer Type
                    </p>
                    <span className={`inline-flex items-center px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm font-medium ${
                      customer?.customer_type === 'vip' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' :
                      customer?.customer_type === 'business' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                      customer?.customer_type === 'wholesale' ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400' :
                      customer?.customer_type === 'regular' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                      customer?.customer_type === 'home' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400' :
                      'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                    }`}>
                      {customer?.customer_type === 'vip' ? 'VIP' :
                       customer?.customer_type === 'business' ? 'Business' :
                       customer?.customer_type === 'wholesale' ? 'Wholesale' :
                       customer?.customer_type === 'regular' ? 'Regular' :
                       customer?.customer_type === 'home' ? 'Home' :
                       'Retail'}
                    </span>
                  </div>
                  
                  <div className="sm:col-span-2">
                    <p className={`text-xs sm:text-sm font-medium mb-1 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      Address
                    </p>
                    <p className={`text-sm sm:text-base break-words ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      {customer?.address || 'N/A'}
                    </p>
                  </div>
                  
                  <div>
                    <p className={`text-xs sm:text-sm font-medium mb-1 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      Status
                    </p>
                    <span className={`inline-flex items-center px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm font-medium ${
                      customer?.is_active
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                    }`}>
                      {customer?.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  
                  <div>
                    <p className={`text-xs sm:text-sm font-medium mb-1 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      Outstanding Balance
                    </p>
                    <p className={`text-base sm:text-lg font-semibold ${
                      (customer?.balance_amount || 0) > 0
                        ? 'text-red-600'
                        : 'text-green-600'
                    }`}>
                      {formatCurrency(customer?.balance_amount || 0)}
                    </p>
                  </div>
                  
                  <div className="sm:col-span-2">
                    <p className={`text-xs sm:text-sm font-medium mb-1 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      Registered Date
                    </p>
                    <p className={`text-sm sm:text-base ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      {customer?.created_at ? new Date(customer.created_at).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Sales History */}
              <div className={`rounded-lg border ${
                theme === 'dark'
                  ? 'bg-slate-800 border-slate-700'
                  : 'bg-white border-gray-200'
              }`}>
                <div className="p-2.5 sm:p-3 md:p-4 border-b border-gray-200 dark:border-slate-700">
                  <div className="flex items-center space-x-2">
                    <ShoppingCart className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${
                      theme === 'dark' ? 'text-green-400' : 'text-green-600'
                    }`} />
                    <h3 className={`text-sm sm:text-base font-semibold ${
                      theme === 'dark' ? 'text-white' : 'text-slate-900'
                    }`}>
                      Sales History
                    </h3>
                  </div>
                </div>
                
                <div className="p-3 sm:p-4 md:p-6">
                  {dataLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    </div>
                  ) : customerSales.length > 0 ? (
                    <div className="overflow-x-auto -mx-3 sm:-mx-4 md:-mx-6 px-3 sm:px-4 md:px-6">
                      <div className="inline-block min-w-full align-middle">
                        <div className="overflow-hidden">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
                            <thead>
                              <tr className={`${
                                theme === 'dark' ? 'bg-slate-700/50' : 'bg-gray-50'
                              }`}>
                                <th className={`px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium uppercase tracking-wider ${
                                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                                  Date
                                </th>
                                <th className={`px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium uppercase tracking-wider ${
                                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                                  Total
                                </th>
                                <th className={`px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium uppercase tracking-wider ${
                                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                                  Paid
                                </th>
                                <th className={`px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium uppercase tracking-wider ${
                                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                                  Due
                                </th>
                                <th className={`px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium uppercase tracking-wider ${
                                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                                  Status
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                              {customerSales.map((sale, index) => (
                                <tr key={sale.id || index} className={`${
                                  theme === 'dark' ? 'hover:bg-slate-700/30' : 'hover:bg-gray-50'
                                }`}>
                                  <td className={`px-2 sm:px-3 py-2 sm:py-3 whitespace-nowrap text-xs sm:text-sm ${
                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                  }`}>
                                    {new Date(sale.created_at).toLocaleDateString()}
                                  </td>
                                  <td className={`px-2 sm:px-3 py-2 sm:py-3 whitespace-nowrap text-xs sm:text-sm font-medium ${
                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                  }`}>
                                    {formatCurrency(sale.total_amount || 0)}
                                  </td>
                                  <td className={`px-2 sm:px-3 py-2 sm:py-3 whitespace-nowrap text-xs sm:text-sm ${
                                    theme === 'dark' ? 'text-green-400' : 'text-green-600'
                                  }`}>
                                    {formatCurrency(sale.paid_amount || 0)}
                                  </td>
                                  <td className={`px-2 sm:px-3 py-2 sm:py-3 whitespace-nowrap text-xs sm:text-sm ${
                                    (sale.due_amount || 0) > 0
                                      ? 'text-red-600'
                                      : 'text-gray-500'
                                  }`}>
                                    {formatCurrency(sale.due_amount || 0)}
                                  </td>
                                  <td className="px-2 sm:px-3 py-2 sm:py-3 whitespace-nowrap">
                                    <span className={`inline-flex items-center px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium ${
                                      sale.payment_status === 'paid'
                                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                        : sale.payment_status === 'partial'
                                        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                                        : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                    }`}>
                                      {(sale.payment_status || 'pending').charAt(0).toUpperCase() + (sale.payment_status || 'pending').slice(1)}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className={`text-center py-8 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      <ShoppingCart className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 opacity-50" />
                      <p className="text-sm sm:text-base">No sales history found</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment History */}
              <div className={`rounded-lg border ${
                theme === 'dark'
                  ? 'bg-slate-800 border-slate-700'
                  : 'bg-white border-gray-200'
              }`}>
                <div className="p-2.5 sm:p-3 md:p-4 border-b border-gray-200 dark:border-slate-700">
                  <div className="flex items-center space-x-2">
                    <CreditCard className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${
                      theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                    }`} />
                    <h3 className={`text-sm sm:text-base font-semibold ${
                      theme === 'dark' ? 'text-white' : 'text-slate-900'
                    }`}>
                      Payment History
                    </h3>
                  </div>
                </div>
                
                <div className="p-2.5 sm:p-3 md:p-4">
                  {dataLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    </div>
                  ) : paymentHistory.length > 0 ? (
                    <div className="overflow-x-auto -mx-2.5 sm:-mx-3 md:-mx-4 px-2.5 sm:px-3 md:px-4">
                      <div className="inline-block min-w-full align-middle">
                        <div className="overflow-hidden">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
                            <thead>
                              <tr className={`${
                                theme === 'dark' ? 'bg-slate-700/50' : 'bg-gray-50'
                              }`}>
                                <th className={`px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium uppercase tracking-wider ${
                                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                                  Date
                                </th>
                                <th className={`px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium uppercase tracking-wider ${
                                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                                  Amount
                                </th>
                                <th className={`px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium uppercase tracking-wider ${
                                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                                  Notes
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                              {paymentHistory.map((payment, index) => (
                                <tr key={payment.id || index} className={`${
                                  theme === 'dark' ? 'hover:bg-slate-700/30' : 'hover:bg-gray-50'
                                }`}>
                                  <td className={`px-2 sm:px-3 py-2 sm:py-3 whitespace-nowrap text-xs sm:text-sm ${
                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                  }`}>
                                    {new Date(payment.created_at).toLocaleDateString()}
                                  </td>
                                  <td className={`px-2 sm:px-3 py-2 sm:py-3 whitespace-nowrap text-xs sm:text-sm font-medium text-green-600`}>
                                    {formatCurrency(payment.amount || 0)}
                                  </td>
                                  <td className={`px-2 sm:px-3 py-2 sm:py-3 text-xs sm:text-sm ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                                  }`}>
                                    <div className="max-w-xs truncate">
                                      {payment.notes || 'N/A'}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className={`text-center py-8 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      <CreditCard className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 opacity-50" />
                      <p className="text-sm sm:text-base">No payment history found</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            /* Create/Edit Mode - Form */
            isLoading ? (
              <form id="customer-form">
                <div className="space-y-2.5 sm:space-y-3 md:space-y-4">
                  {/* Basic Information Skeleton */}
                  <div className="space-y-2.5 sm:space-y-3">
                    <div className={`h-5 w-32 rounded animate-pulse ${
                      theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                    }`} />
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 sm:gap-3">
                      {/* Customer Name Skeleton */}
                      <div>
                        <div className={`h-4 w-24 rounded animate-pulse mb-2 ${
                          theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                        }`} />
                        <div className={`w-full h-10 rounded-lg animate-pulse ${
                          theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                        }`} />
                      </div>

                      {/* Phone Skeleton */}
                      <div>
                        <div className={`h-4 w-24 rounded animate-pulse mb-2 ${
                          theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                        }`} />
                        <div className={`w-full h-10 rounded-lg animate-pulse ${
                          theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                        }`} />
                      </div>
                    </div>

                    {/* Address Skeleton */}
                    <div>
                      <div className={`h-4 w-24 rounded animate-pulse mb-2 ${
                        theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                      }`} />
                      <div className={`w-full h-24 rounded-lg animate-pulse ${
                        theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                      }`} />
                    </div>
                  </div>
                </div>
              </form>
            ) : (
            <form onSubmit={handleSubmit} id="customer-form" className="animate-content-fade">
              <div className="space-y-2.5 sm:space-y-3 md:space-y-4">
                {/* Basic Information */}
                <div className="space-y-2.5 sm:space-y-3">
                  <h3 className={`text-sm sm:text-base font-medium flex items-center space-x-2 ${
                    theme === 'dark' ? 'text-white' : 'text-slate-900'
                  }`}>
                    <User className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span>Customer Information</span>
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 sm:gap-3">
                    {/* Customer Name */}
                    <div>
                      <label htmlFor="name" className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${
                        theme === 'dark' ? 'text-slate-300' : 'text-slate-700'
                      }`}>
                        Customer Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 border rounded-lg transition-colors touch-manipulation text-sm ${
                          errors.name 
                            ? 'border-red-300' 
                            : theme === 'dark' 
                              ? 'border-slate-600 bg-slate-700 text-white' 
                              : 'border-slate-300 bg-white text-gray-900'
                        }`}
                        placeholder="Enter customer name"
                      />
                      {errors.name && (
                        <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center space-x-1">
                          <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span>{errors.name}</span>
                        </p>
                      )}
                    </div>

                    {/* Phone */}
                    <div>
                      <label htmlFor="phone" className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${
                        theme === 'dark' ? 'text-slate-300' : 'text-slate-700'
                      }`}>
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 border rounded-lg transition-colors touch-manipulation text-sm ${
                          theme === 'dark' 
                            ? 'border-slate-600 bg-slate-700 text-white' 
                            : 'border-slate-300 bg-white text-gray-900'
                        }`}
                        placeholder="Enter phone number"
                      />
                    </div>

                    {/* Customer Type */}
                    <div className="md:col-span-2">
                      <label htmlFor="customer_type" className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${
                        theme === 'dark' ? 'text-slate-300' : 'text-slate-700'
                      }`}>
                        Customer Type *
                      </label>
                      <select
                        id="customer_type"
                        name="customer_type"
                        value={formData.customer_type}
                        onChange={(e) => handleInputChange('customer_type', e.target.value)}
                        className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 border rounded-lg transition-colors touch-manipulation text-sm ${
                          theme === 'dark' 
                            ? 'border-slate-600 bg-slate-700 text-white' 
                            : 'border-slate-300 bg-white text-gray-900'
                        }`}
                      >
                        <option value="retail">Retail (Individual Customer)</option>
                        <option value="home">Home (Personal/Home-based)</option>
                        <option value="wholesale">Wholesale (Bulk Buyer)</option>
                        <option value="business">Business (Registered Company)</option>
                        <option value="regular">Regular (Frequent Customer)</option>
                        <option value="vip">VIP (Premium Customer)</option>
                      </select>
                    </div>

                    {/* Address */}
                    <div className="md:col-span-2">
                      <label htmlFor="address" className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${
                        theme === 'dark' ? 'text-slate-300' : 'text-slate-700'
                      }`}>
                        Address
                      </label>
                      <textarea
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        rows={3}
                        className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 border rounded-lg transition-colors resize-none touch-manipulation text-sm ${
                          theme === 'dark' 
                            ? 'border-slate-600 bg-slate-700 text-white' 
                            : 'border-slate-300 bg-white text-gray-900'
                        }`}
                        placeholder="Enter address"
                      />
                    </div>

                    {/* Active Status Checkboxes */}
                    <div className="md:col-span-2">
                      <label className={`block text-xs sm:text-sm font-medium mb-2.5 sm:mb-3 ${
                        theme === 'dark' ? 'text-slate-300' : 'text-slate-700'
                      }`}>
                        Status
                      </label>
                      <div className="flex gap-4 sm:gap-6">
                        {/* Active Checkbox */}
                        <label className="flex items-center gap-2.5 cursor-pointer group">
                          <div className="relative flex items-center justify-center">
                            <input
                              type="checkbox"
                              checked={formData.is_active === true}
                              onChange={() => handleInputChange('is_active', 'true')}
                              className="sr-only"
                            />
                            <div className={`w-5 h-5 rounded border-2 transition-all ${
                              formData.is_active === true
                                ? 'bg-primary-600 border-primary-700'
                                : theme === 'dark'
                                ? 'border-slate-500 bg-slate-700 group-hover:border-slate-400'
                                : 'border-slate-300 bg-white group-hover:border-slate-400'
                            }`}>
                              {formData.is_active === true && (
                                <CheckCircle2 size={18} className="text-white absolute" />
                              )}
                            </div>
                          </div>
                          <span className={`text-sm font-medium transition-colors ${
                            formData.is_active === true
                              ? theme === 'dark'
                                ? 'text-primary-400'
                                : 'text-primary-600'
                              : theme === 'dark'
                              ? 'text-slate-400 group-hover:text-slate-300'
                              : 'text-slate-600 group-hover:text-slate-700'
                          }`}>
                            Active
                          </span>
                        </label>

                        {/* Inactive Checkbox */}
                        <label className="flex items-center gap-2.5 cursor-pointer group">
                          <div className="relative flex items-center justify-center">
                            <input
                              type="checkbox"
                              checked={formData.is_active === false}
                              onChange={() => handleInputChange('is_active', 'false')}
                              className="sr-only"
                            />
                            <div className={`w-5 h-5 rounded border-2 transition-all ${
                              formData.is_active === false
                                ? theme === 'dark'
                                  ? 'bg-slate-500 border-slate-600'
                                  : 'bg-slate-400 border-slate-500'
                                : theme === 'dark'
                                ? 'border-slate-500 bg-slate-700 group-hover:border-slate-400'
                                : 'border-slate-300 bg-white group-hover:border-slate-400'
                            }`}>
                              {formData.is_active === false && (
                                <XCircle size={18} className="text-white absolute" />
                              )}
                            </div>
                          </div>
                          <span className={`text-sm font-medium transition-colors ${
                            formData.is_active === false
                              ? theme === 'dark'
                                ? 'text-slate-300'
                                : 'text-slate-700'
                              : theme === 'dark'
                              ? 'text-slate-400 group-hover:text-slate-300'
                              : 'text-slate-600 group-hover:text-slate-700'
                          }`}>
                            Inactive
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
            )
          )}
          </div>
        </div>

        {/* Footer with Action Buttons - Only show for create/edit modes */}
        {mode !== 'view' && (
          <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-t flex-shrink-0 rounded-b-xl ${
            theme === 'dark' ? 'border-slate-700 bg-slate-800/95' : 'border-slate-200 bg-gray-50/95'
          }`}>
            <div className="flex flex-col-reverse sm:flex-row gap-3">
              {loading || isLoading ? (
                <>
                  <div className={`w-full sm:flex-1 h-11 rounded-xl animate-pulse ${
                    theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                  }`} />
                  <div className={`w-full sm:flex-1 h-11 rounded-xl animate-pulse ${
                    theme === 'dark' ? 'bg-slate-600' : 'bg-gray-200'
                  }`} />
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleClose}
                    className={`w-full sm:flex-1 px-4 py-2.5 text-sm font-medium rounded-xl border transition-all duration-200 touch-manipulation ${
                      theme === 'dark'
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700 hover:border-gray-500'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    form="customer-form"
                    disabled={loading}
                    className={`w-full sm:flex-1 px-4 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 touch-manipulation ${
                      loading
                        ? theme === 'dark'
                          ? 'bg-slate-600 text-slate-400 cursor-not-allowed opacity-50'
                          : 'bg-slate-400 text-white cursor-not-allowed opacity-50'
                        : theme === 'dark'
                          ? 'bg-blue-600 hover:bg-blue-700 text-white'
                          : 'bg-blue-500 hover:bg-blue-600 text-white'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {loading ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : null}
                    <span>{loading ? 'Processing...' : mode === 'create' ? 'Create Customer' : 'Update Customer'}</span>
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Footer for View Mode - Close button only */}
        {mode === 'view' && (
          <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-t flex-shrink-0 rounded-b-xl ${
            theme === 'dark' ? 'border-slate-700 bg-slate-800/95' : 'border-slate-200 bg-gray-50/95'
          }`}>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleClose}
                className={`px-6 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl touch-manipulation ${
                  theme === 'dark'
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                }`}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default CustomerModal;
